import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';

interface Column<T> {
  key: string;
  header: string;
  width?: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (item: T) => void;
  isLoading?: boolean;
  pagination?: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  };
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
}

function Table<T>({
  columns,
  data,
  onRowClick,
  isLoading,
  pagination,
  sortColumn,
  sortDirection,
  onSortChange,
}: TableProps<T>) {
  const handleSortClick = (column: Column<T>) => {
    if (!column.sortable || !onSortChange) return;

    const newDirection = column.key === sortColumn
      ? sortDirection === 'asc' ? 'desc' : 'asc'
      : 'asc';

    onSortChange(column.key, newDirection);
  };

  const renderSortIcon = (column: Column<T>) => {
    if (!column.sortable) return null;

    if (column.key === sortColumn) {
      return sortDirection === 'asc' 
        ? <ArrowUp className="w-4 h-4" />
        : <ArrowDown className="w-4 h-4" />;
    }

    return <ChevronsUpDown className="w-4 h-4 text-[#98A2B3]" />;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="py-8 text-center text-[#667085]">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7F56D9] border-t-transparent mx-auto mb-4"></div>
          Loading...
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="py-8 text-center text-[#667085]">
          No data available
        </div>
      );
    }

    return null;
  };

  const loadingOrEmptyState = renderContent();

  const renderPaginationNumbers = () => {
    if (!pagination) return null;

    const { currentPage, totalPages, onPageChange } = pagination;
    const pageNumbers: (number | string)[] = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(currentPage - halfVisible, 1);
    let endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(endPage - maxVisiblePages + 1, 1);
    }

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) pageNumbers.push('...');
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageNumbers.push('...');
      pageNumbers.push(totalPages);
    }

    return (
      <div className="hidden sm:flex items-center space-x-1">
        {pageNumbers.map((number, index) => (
          number === '...' ? (
            <span
              key={`ellipsis-${index}`}
              className="w-10 h-10 flex items-center justify-center text-[#667085]"
            >
              <MoreHorizontal className="w-5 h-5" />
            </span>
          ) : (
            <button
              key={number}
              onClick={() => onPageChange(number as number)}
              disabled={currentPage === number}
              className={`
                w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium
                transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
                ${currentPage === number
                  ? 'bg-[#7F56D9] text-white'
                  : 'text-[#344054] hover:bg-[#F9FAFB] focus:ring-[#7F56D9]'
                }
              `}
            >
              {number}
            </button>
          )
        ))}
      </div>
    );
  };

  return (
    <div className="w-full">
      {loadingOrEmptyState ? (
        loadingOrEmptyState
      ) : (
        <>
          {/* Card view for mobile */}
          <div className="block md:hidden space-y-4">
            {data.map((item, index) => (
              <div
                key={index}
                onClick={() => onRowClick?.(item)}
                className={`
                  bg-white rounded-lg border border-[#EAECF0] p-4 space-y-3
                  ${onRowClick ? 'cursor-pointer hover:bg-[#F9FAFB]' : ''}
                `}
              >
                {columns.map((column) => (
                  <div key={column.key} className="flex flex-col">
                    <span className="text-sm font-medium text-[#667085] mb-1">
                      {column.header}
                    </span>
                    <span className="text-sm text-[#101828]">
                      {column.render ? column.render(item) : (item as any)[column.key]}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Table view for desktop */}
          <div className="hidden md:block overflow-x-auto border border-[#EAECF0] rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#EAECF0] bg-[#F9FAFB]">
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`
                        text-left py-3 px-6 text-xs font-medium text-[#667085] uppercase tracking-wider
                        ${column.width ? `w-[${column.width}]` : ''}
                        ${column.sortable ? 'cursor-pointer hover:bg-[#F0F2F5]' : ''}
                      `}
                      onClick={() => column.sortable && handleSortClick(column)}
                    >
                      <div className="flex items-center gap-2">
                        {column.header}
                        {renderSortIcon(column)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EAECF0] bg-white">
                {data.map((item, index) => (
                  <tr
                    key={index}
                    onClick={() => onRowClick?.(item)}
                    className={`
                      ${onRowClick ? 'cursor-pointer hover:bg-[#F9FAFB]' : ''}
                    `}
                  >
                    {columns.map((column) => (
                      <td key={column.key} className="py-4 px-6 text-sm text-[#101828]">
                        {column.render ? column.render(item) : (item as any)[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Enhanced Pagination */}
      {pagination && !isLoading && data.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between mt-4 px-4 sm:px-6 gap-4">
          <div className="text-sm text-[#667085] order-2 sm:order-1">
            Page {pagination.currentPage} of {pagination.totalPages}
          </div>
          <div className="flex items-center space-x-2 order-1 sm:order-2">
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="
                px-3.5 py-2 border border-[#D0D5DD] rounded-lg text-[#344054]
                transition-colors duration-200
                hover:bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white
                flex items-center
              "
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </button>

            {renderPaginationNumbers()}

            <button
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="
                px-3.5 py-2 border border-[#D0D5DD] rounded-lg text-[#344054]
                transition-colors duration-200
                hover:bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white
                flex items-center
              "
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Table;